import React, { useState, useRef, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useStyles from "./Notification.styles";
import {
  readNotificationStart,
  getNotificationsStart,
  selectNotifications
} from "../../../redux-saga/redux/notifications/notifications";
import NotificationBadge from "./NotificationBadge";

const PAGE_LIMIT = 10;

const _htmlParser = typeof DOMParser !== "undefined" ? new DOMParser() : null;

const decodeHtmlEntities = (value = "") => {
  if (!value || !_htmlParser) return value;
  let decoded = _htmlParser.parseFromString(value, "text/html").body.textContent || "";
  if (decoded.includes("&")) {
    decoded = _htmlParser.parseFromString(decoded, "text/html").body.textContent || decoded;
  }
  return decoded;
};

const getPlainText = (value = "") =>
  decodeHtmlEntities(String(value).replace(/<[^>]*>/g, " "))
    .replace(/\u00a0/g, " ")
    .replace(/&nbsp;/gi, " ");

const NotificationDropdown = ({ onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const notificationsState = useSelector(selectNotifications) || {};
  const { notifications = [], loading = false, pagination = {} } = notificationsState;
  const lang = (i18n.language || "en")
    .split(/[-_]/)[0]
    .toLowerCase();
  const [expandedNotifications, setExpandedNotifications] = useState({});
  const containerRef = useRef(null);
  const sentinelRef = useRef(null);

  const currentPage = pagination.page || 1;
  const totalPages = pagination.totalPages || 1;
  const hasMore = currentPage < totalPages;

  const loadingRef = useRef(loading);
  const currentPageRef = useRef(currentPage);
  loadingRef.current = loading;
  currentPageRef.current = currentPage;

  const getLocalizedValue = (value) => {
    if (value && typeof value === "object") {
      const entries = Object.entries(value);
      const matched = entries.find(([key]) => key.toLowerCase() === lang);
      return (matched && matched[1]) || value.en || value.EN || "";
    }
    return value || "";
  };

  const toggleExpanded = (notificationId) => {
    setExpandedNotifications((prevState) => ({
      ...prevState,
      [notificationId]: !prevState[notificationId]
    }));
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = containerRef.current;
    if (!sentinel || !container || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          dispatch(getNotificationsStart({ pageNumber: currentPageRef.current + 1, limit: PAGE_LIMIT }));
        }
      },
      { root: container, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, dispatch]);

  const visibleNotifications = notifications.filter(
    (notif) => !(notif.notificationExpireDate && new Date(notif.notificationExpireDate) < new Date())
  );

  if (loading && visibleNotifications.length === 0) {
    return (
      <Box className={classes.dropdownContainer} style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100px" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!loading && visibleNotifications.length === 0) {
    return (
      <Box className={classes.dropdownContainer}>
        <Box className={classes.notificationItem}>
          <Typography className={classes.description}>
            {t("LOBBY.NO_NOTIFICATIONS_AVAILABLE")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} className={classes.dropdownContainer}>
      {visibleNotifications.map((notif) => {
        const nId = notif.id;
        const title = getPlainText(getLocalizedValue(notif.title));
        const message = getPlainText(getLocalizedValue(notif.notificationMessage));
        const userNotificationTypeName = notif.UserNotificationType?.name || {};
        const badgeLabel = getPlainText(getLocalizedValue(userNotificationTypeName));
        const badgeType = notif.referenceType || "";
        const isExpanded = Boolean(expandedNotifications[nId]);
        const isExpandable = message.length > 90;
        const isBonus = String(notif.referenceType || "").toUpperCase().includes("BONUS");
        const hasBadge = !!(badgeLabel || badgeType);
        return (
          <Box
            onClick={() => {
              dispatch(readNotificationStart({ notificationIds: [nId] }));
              if (notif.redirectionLink) {
                if (/^https?:\/\//.test(notif.redirectionLink)) {
                  window.open(notif.redirectionLink, "_blank");
                } else {
                  navigate(notif.redirectionLink);
                }
              }
              onClose?.();
            }}
            sx={{ cursor: "pointer" }}
            key={nId}
            className={classes.notificationItem}
          >
            <Box className={classes.iconContainer}>
              <img
                style={{ height: "20px", width: "20px" }}
                src={isBonus ? "/images/bonus-icon.png" : "/images/notification.png"}
                alt={isBonus ? "bonus" : "notification"}
              />
            </Box>
            <Box className={classes.textContainer}>
              <Typography className={classes.title}>
                {title || t("LOBBY.NEW_NOTIFICATION")}
              </Typography>
              <Typography className={`${classes.description} ${isExpanded ? classes.descriptionExpanded : ""}`}>
                {message}
              </Typography>
              {isExpandable && (
                <Typography
                  className={classes.showMoreText}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleExpanded(nId);
                  }}
                >
                  {isExpanded ? t("LOBBY.SHOW_LESS") : t("LOBBY.SHOW_MORE")}
                </Typography>
              )}
            </Box>
            {hasBadge && (
              <Box sx={{ flexShrink: 0, alignSelf: "center", marginRight: "-20px" }}>
                <NotificationBadge type={badgeType} label={badgeLabel} />
              </Box>
            )}
          </Box>
        );
      })}
      {(hasMore || loading) && (
        <Box ref={hasMore ? sentinelRef : undefined} style={{ display: "flex", justifyContent: "center", padding: "8px" }}>
          <CircularProgress size={18} style={{ color: theme?.palette?.secondaryBG?.main }} />
        </Box>
      )}
    </Box>
  );
};

export default NotificationDropdown;
